import { Box } from '@televet/kibble-ui/chakra';
import React, { useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { EChartRenderer } from './EChartRenderer';
import { OttoFlowUpsellCTA } from './OttoFlowUpsellCTA';
import {
  OttoFollowUpSuggestions,
  isValidFollowUpSuggestion,
} from './OttoFollowUpSuggestions';
import { ExportableTable } from './ExportableList';
import * as echarts from 'echarts';
import { LLMJSONParser } from 'ai-json-fixer';

interface MarkdownMessageProps {
  content: string;
  fontSize?: string;
  onSuggestionClick?: (question: string) => void;
  /**
   * Opens the workflow self-enrollment detail view. Injected by the consuming app
   * (this package has no tRPC dependency); omitting it renders enrollment CTAs
   * disabled, which is what `apps/admin`'s read-only surfaces want.
   */
  onWorkflowEnrollClick?: (workflowKey: string) => void;
  /**
   * Whether a workflow-enrollment suggestion should render. Injected the same
   * way as `onWorkflowEnrollClick`; omitting it shows every suggestion.
   */
  isWorkflowActionVisible?: (workflowKey: string) => boolean;
  /**
   * Opens the host's support chat for a support-handoff chip. Injected the same way as
   * `onWorkflowEnrollClick`; omitting it renders an email link in place of the chip.
   */
  onSupportHandoffClick?: () => void;
  suggestionsDisabled?: boolean;
}

/**
 * MarkdownMessage component that supports rendering markdown content with chart support.
 *
 * To embed a chart, use a code block with language "chart" or "echarts" containing JSON chart configuration:
 *
 * ```chart
 * {
 *   "xAxis": { "type": "category", "data": ["Mon", "Tue", "Wed"] },
 *   "yAxis": { "type": "value" },
 *   "series": [{ "data": [120, 200, 150], "type": "bar" }],
 *   "height": "400px",
 *   "minHeight": "300px"
 * }
 * ```
 *
 * The chart configuration follows ECharts options format.
 * Optional: include "height" and "minHeight" properties to control chart dimensions.
 */

// Simple hash function for generating stable keys
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}

// Initialize LLM JSON parser for better handling of LLM-generated JSON
const aiParser = new LLMJSONParser();

export function MarkdownMessage({
  content,
  fontSize = 'sm',
  onSuggestionClick,
  onWorkflowEnrollClick,
  isWorkflowActionVisible,
  onSupportHandoffClick,
  suggestionsDisabled,
}: MarkdownMessageProps) {
  // Cache for parsed chart options to prevent unnecessary re-renders
  // Key: hash of complete valid JSON, Value: chart config with stable option reference
  const chartCacheRef = React.useRef<
    Map<
      string,
      {
        options: echarts.EChartsOption;
        height: string;
        minHeight: string;
        completeJson: string; // Store the complete JSON for matching during streaming
      }
    >
  >(new Map());

  // Track which content strings map to which chart keys
  // This helps us find the right chart during streaming when JSON is incomplete
  const contentToKeyRef = React.useRef<Map<string, string>>(new Map());

  // Memoized pre component handler to prevent ReactMarkdown from seeing it as new
  const PreComponent = useCallback(({ node, children, ...props }: any) => {
    // Extract code element from children
    // In react-markdown v10, children is typically a single code element
    const codeElement = React.Children.toArray(children)[0] as any;

    // Try to get the code element props
    const codeProps = codeElement?.props || codeElement;
    const className = codeProps?.className || '';
    let language = className.replace('language-', '').trim();

    // Handle escaped language tags (e.g., "chart\n" -> "chart")
    language = language.replace(/\\n/g, '').trim();

    // Get code content - could be string or array
    let codeContent = '';
    if (typeof codeProps?.children === 'string') {
      codeContent = codeProps.children;
    } else if (Array.isArray(codeProps?.children)) {
      codeContent = codeProps.children.join('');
    } else if (codeProps?.children) {
      codeContent = String(codeProps.children);
    }

    // Check if it's a chart code block (language starts with "chart" or "echarts")
    if (
      language &&
      (language.startsWith('chart') || language === 'echarts') &&
      codeContent.trim()
    ) {
      // Normalize content and fix invalid JSON with literal newlines in strings
      let normalizedContent = codeContent.trim();
      // First, escape literal newlines that are inside JSON strings (between quotes)
      // This handles cases where LLMs output JSON with literal newlines in string values
      // which is invalid JSON syntax - we need to escape them before parsing
      normalizedContent = normalizedContent.replace(
        /"([^"\\]|\\.)*"/g,
        (match) => {
          // Replace literal newlines inside string values with escaped newlines
          return match.replace(/\n/g, '\\n');
        }
      );
      if (normalizedContent[0] !== '{') {
        normalizedContent = `{${normalizedContent}`;
      }
      const trimmedContent = normalizedContent.split('```')[0];

      // FIRST: Check if we have a cached chart for this content (or a prefix match)
      // This prevents flashing during streaming by showing cached chart immediately
      let stableKey: string | undefined;
      let cachedChart:
        | {
            options: echarts.EChartsOption;
            height: string;
            minHeight: string;
            completeJson: string;
          }
        | undefined;

      // Check if we've seen this exact content before
      stableKey = contentToKeyRef.current.get(trimmedContent);
      if (stableKey) {
        cachedChart = chartCacheRef.current.get(stableKey);
      }

      // If not found, check if any cached chart's JSON starts with current content
      // This handles streaming where content is a prefix of the complete JSON
      if (!cachedChart) {
        for (const [key, chart] of chartCacheRef.current.entries()) {
          if (chart.completeJson.startsWith(trimmedContent)) {
            stableKey = key;
            cachedChart = chart;
            contentToKeyRef.current.set(trimmedContent, stableKey);
            break;
          }
        }
      }

      // If we have a cached chart, render it immediately without parsing
      if (cachedChart && stableKey) {
        return React.createElement(
          Box,
          { margin: '1em 0', key: stableKey },
          React.createElement(EChartRenderer, {
            options: cachedChart.options,
            height: cachedChart.height,
            minHeight: cachedChart.minHeight,
          })
        );
      }

      // No cached chart found - try to parse and create new chart
      try {
        // Parse chart configuration - supports both JSON and JavaScript object literals with functions
        let chartOptions: any;
        try {
          // First try LLM JSON parser (handles malformed JSON from LLMs better)
          const parsed = aiParser.parse<any>(trimmedContent);
          if (parsed) {
            chartOptions = parsed;
          } else {
            // Fallback to standard JSON parsing if LLM parser returns null
            chartOptions = JSON.parse(trimmedContent);
          }
        } catch (jsonError) {
          console.log('failed content', trimmedContent);
          // If JSON parsing fails, try parsing as JavaScript (handles functions)
          // Replace function definitions with placeholders, parse JSON, then restore functions
          const functionPlaceholders: Array<{
            placeholder: string;
            funcString: string;
          }> = [];
          let contentWithPlaceholders = trimmedContent;
          let placeholderIndex = 0;

          // Helper to find matching closing brace for nested braces
          const findMatchingBrace = (
            str: string,
            startIndex: number
          ): number => {
            let depth = 0; // Will be incremented when we encounter the opening brace
            let inString = false;
            let stringChar = '';
            for (let i = startIndex; i < str.length; i++) {
              const char = str[i];
              const prevChar = i > 0 ? str[i - 1] : '';

              // Handle string literals
              if (
                (char === '"' || char === "'" || char === '`') &&
                prevChar !== '\\'
              ) {
                if (!inString) {
                  inString = true;
                  stringChar = char;
                } else if (char === stringChar) {
                  inString = false;
                }
                continue;
              }

              if (inString) continue;

              if (char === '{') depth++;
              if (char === '}') {
                depth--;
                if (depth === 0) return i;
              }
            }
            return -1;
          };

          // Match function expressions: function(params) { ... }
          const functionPattern = /function\s*\([^)]*\)\s*\{/g;
          let match;
          const functionMatches: Array<{
            start: number;
            end: number;
            funcString: string;
          }> = [];

          // Find all function matches and their end positions
          while ((match = functionPattern.exec(trimmedContent)) !== null) {
            const start = match.index;
            const end = findMatchingBrace(
              trimmedContent,
              start + match[0].length - 1
            );
            if (end !== -1) {
              const funcString = trimmedContent.substring(start, end + 1);
              functionMatches.push({ start, end, funcString });
            }
          }

          // Replace functions from end to start to preserve indices
          functionMatches.reverse().forEach((funcMatch) => {
            const placeholder = `__FUNCTION_PLACEHOLDER_${placeholderIndex++}__`;
            functionPlaceholders.push({
              placeholder,
              funcString: funcMatch.funcString,
            });
            contentWithPlaceholders =
              contentWithPlaceholders.substring(0, funcMatch.start) +
              `"${placeholder}"` +
              contentWithPlaceholders.substring(funcMatch.end + 1);
          });

          // Match arrow functions: (params) => { ... }
          const arrowFunctionPattern = /\([^)]*\)\s*=>\s*\{/g;
          const arrowMatches: Array<{
            start: number;
            end: number;
            funcString: string;
          }> = [];

          while (
            (match = arrowFunctionPattern.exec(contentWithPlaceholders)) !==
            null
          ) {
            const start = match.index;
            const end = findMatchingBrace(
              contentWithPlaceholders,
              start + match[0].length - 1
            );
            if (end !== -1) {
              const funcString = contentWithPlaceholders.substring(
                start,
                end + 1
              );
              arrowMatches.push({ start, end, funcString });
            }
          }

          // Replace arrow functions from end to start
          arrowMatches.reverse().forEach((funcMatch) => {
            const placeholder = `__FUNCTION_PLACEHOLDER_${placeholderIndex++}__`;
            functionPlaceholders.push({
              placeholder,
              funcString: funcMatch.funcString,
            });
            contentWithPlaceholders =
              contentWithPlaceholders.substring(0, funcMatch.start) +
              `"${placeholder}"` +
              contentWithPlaceholders.substring(funcMatch.end + 1);
          });

          // Now parse as JSON
          try {
            // First try LLM JSON parser (handles malformed JSON from LLMs better)
            const parsed = aiParser.parse<any>(contentWithPlaceholders);
            if (parsed) {
              chartOptions = parsed;
            } else {
              // Fallback to standard JSON parsing if LLM parser returns null
              chartOptions = JSON.parse(contentWithPlaceholders);
            }

            // Restore functions by recursively traversing the object
            const restoreFunctions = (obj: any): any => {
              if (
                typeof obj === 'string' &&
                obj.startsWith('__FUNCTION_PLACEHOLDER_')
              ) {
                const placeholder = functionPlaceholders.find(
                  (p) => p.placeholder === obj
                );
                if (placeholder) {
                  // Evaluate the function string safely
                  try {
                    return new Function('return ' + placeholder.funcString)();
                  } catch (funcError) {
                    console.error(
                      '[MarkdownMessage] Error evaluating function:',
                      funcError,
                      placeholder.funcString
                    );
                    return null;
                  }
                }
              }
              if (Array.isArray(obj)) {
                return obj.map(restoreFunctions);
              }
              if (obj && typeof obj === 'object') {
                const restored: any = {};
                for (const key in obj) {
                  restored[key] = restoreFunctions(obj[key]);
                }
                return restored;
              }
              return obj;
            };

            chartOptions = restoreFunctions(chartOptions);
          } catch (parseError) {
            // If function-aware parsing fails, log and re-throw
            console.error(
              '[MarkdownMessage] Failed to parse chart config with functions:',
              parseError
            );
            console.error(
              '[MarkdownMessage] Content:',
              trimmedContent.substring(0, 200)
            );
            throw jsonError; // Re-throw original JSON error
          }
        }
        const height = chartOptions.height || '400px';
        const minHeight = chartOptions.minHeight || '300px';
        // Remove height/minHeight from options as they're passed separately
        const { height: _, minHeight: __, ...options } = chartOptions;

        // Generate stable key from the complete valid JSON
        stableKey = hashString(trimmedContent);

        // Check if we already have this chart cached
        cachedChart = chartCacheRef.current.get(stableKey);

        if (!cachedChart) {
          // New chart - cache it with stable option reference
          // Deep clone to ensure we have a stable reference that won't be mutated
          // Use a function-preserving deep clone
          const deepClone = (obj: any): any => {
            if (obj === null || typeof obj !== 'object') {
              return obj;
            }
            if (typeof obj === 'function') {
              return obj; // Functions are already references, return as-is
            }
            if (Array.isArray(obj)) {
              return obj.map(deepClone);
            }
            const cloned: any = {};
            for (const key in obj) {
              cloned[key] = deepClone(obj[key]);
            }
            return cloned;
          };
          const stableOptions = deepClone(options) as echarts.EChartsOption;
          cachedChart = {
            options: stableOptions,
            height,
            minHeight,
            completeJson: trimmedContent,
          };
          chartCacheRef.current.set(stableKey, cachedChart);
        }
        // Always use the cached chart reference to ensure stability
        const chartToRender = chartCacheRef.current.get(stableKey)!;

        // Update content-to-key mapping for streaming detection
        contentToKeyRef.current.set(trimmedContent, stableKey);

        // Use React.createElement with stable key to prevent remounting
        try {
          return React.createElement(
            Box,
            { margin: '1em 0', key: stableKey },
            React.createElement(EChartRenderer, {
              options: chartToRender.options,
              height: chartToRender.height,
              minHeight: chartToRender.minHeight,
            })
          );
        } catch (error) {
          console.log('chartToRender', chartToRender);
          console.error('[MarkdownMessage] Error rendering chart:', error);
          throw error;
        }
      } catch (error) {
        const defaultEmptyChartOptions: echarts.EChartsOption = {
          title: {
            text: 'Loading Chart ...',
            left: 'center',
            textStyle: {
              fontSize: 16,
              fontWeight: 'normal',
            },
          },
          xAxis: {
            type: 'category',
            data: [],
            axisLine: {
              show: true,
            },
            axisTick: {
              show: true,
            },
            axisLabel: {
              show: true,
            },
          },
          yAxis: {
            type: 'value',
            axisLine: {
              show: true,
            },
            axisTick: {
              show: true,
            },
            axisLabel: {
              show: true,
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
              },
            },
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true,
          },
          series: [
            {
              type: 'bar',
              data: [],
            },
          ],
        };

        return React.createElement(
          Box,
          { margin: '1em 0' },
          React.createElement(EChartRenderer, {
            options: defaultEmptyChartOptions,
            height: '400px',
            minHeight: '300px',
          })
        );
      }
    }

    // Check if it's a flow-upsell code block
    if (language && language.startsWith('flow-upsell') && codeContent.trim()) {
      try {
        const upsellConfig = JSON.parse(codeContent.trim());
        return React.createElement(
          Box,
          { margin: '1em 0' },
          React.createElement(OttoFlowUpsellCTA, {
            title: upsellConfig.title,
            description: upsellConfig.description,
            learnMoreText: upsellConfig.learnMoreText,
          })
        );
      } catch {
        // Incomplete JSON during streaming — render placeholder
        return React.createElement(
          Box,
          { margin: '1em 0' },
          React.createElement(OttoFlowUpsellCTA, {})
        );
      }
    }

    // Check if it's a follow-up-suggestions code block
    if (language && language.startsWith('follow-up-suggestions') && codeContent.trim()) {
      try {
        const parsed = JSON.parse(codeContent.trim());
        const rawSuggestions = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.suggestions)
            ? parsed.suggestions
            : null;
        if (!rawSuggestions) return null;

        const suggestions = rawSuggestions.filter(isValidFollowUpSuggestion);
        if (suggestions.length === 0) return null;

        const title = typeof parsed?.title === 'string' ? parsed.title : undefined;
        const subtitle = typeof parsed?.subtitle === 'string' ? parsed.subtitle : undefined;

        return React.createElement(OttoFollowUpSuggestions, {
          suggestions,
          onSuggestionClick,
          onWorkflowEnrollClick,
          isWorkflowActionVisible,
          onSupportHandoffClick,
          disabled: suggestionsDisabled,
          title,
          subtitle,
        });
      } catch {
        return null;
      }
    }

    // Default code block rendering
    return (
      <pre
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          padding: '8px',
          borderRadius: '4px',
          overflow: 'auto',
          fontSize: '0.85em',
          margin: '0.5em 0',
        }}
        {...props}
      >
        {children}
      </pre>
    );
  }, [
    onSuggestionClick,
    onWorkflowEnrollClick,
    isWorkflowActionVisible,
    onSupportHandoffClick,
    suggestionsDisabled,
  ]);

  // Memoize all other components to prevent ReactMarkdown from re-rendering
  const components = useMemo(
    () => ({
      pre: PreComponent,
      // Style code blocks
      code: ({ node, className, ...props }: any) => {
        const isInline = !className;
        return isInline ? (
          <code
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              padding: '2px 4px',
              borderRadius: '3px',
              fontSize: '0.9em',
            }}
            {...props}
          />
        ) : (
          <code
            style={{
              display: 'block',
              backgroundColor: 'transparent',
              padding: 0,
              fontSize: '0.85em',
            }}
            {...props}
          />
        );
      },
      // Style paragraphs
      p: ({ node, ...props }: any) => (
        <p style={{ margin: '0.5em 0' }} {...props} />
      ),
      // Style headings
      h1: ({ node, ...props }: any) => (
        <h1
          style={{
            fontSize: '1.5em',
            fontWeight: 'bold',
            margin: '0.75em 0',
          }}
          {...props}
        />
      ),
      h2: ({ node, ...props }: any) => (
        <h2
          style={{
            fontSize: '1.3em',
            fontWeight: 'bold',
            margin: '0.75em 0',
          }}
          {...props}
        />
      ),
      h3: ({ node, ...props }: any) => (
        <h3
          style={{
            fontSize: '1.1em',
            fontWeight: 'bold',
            margin: '0.75em 0',
          }}
          {...props}
        />
      ),
      // Style lists
      ul: ({ node, ...props }: any) => (
        <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} {...props} />
      ),
      ol: ({ node, ...props }: any) => (
        <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} {...props} />
      ),
      // Style links
      a: ({ node, ...props }: any) => (
        <a
          style={{ color: '#3182ce', textDecoration: 'underline' }}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        />
      ),
      // Style blockquotes
      blockquote: ({ node, ...props }: any) => (
        <blockquote
          style={{
            borderLeft: '3px solid #cbd5e0',
            paddingLeft: '1em',
            margin: '0.5em 0',
            fontStyle: 'italic',
            color: '#718096',
          }}
          {...props}
        />
      ),
      // Style tables with CSV export
      table: ({ node, ...props }: any) => <ExportableTable {...props} />,
      thead: ({ node, ...props }: any) => (
        <thead
          style={{
            backgroundColor: '#f7fafc',
            borderBottom: '2px solid #e2e8f0',
          }}
          {...props}
        />
      ),
      tbody: ({ node, ...props }: any) => <tbody {...props} />,
      tr: ({ node, ...props }: any) => (
        <tr
          style={{
            borderBottom: '1px solid #e2e8f0',
          }}
          {...props}
        />
      ),
      th: ({ node, ...props }: any) => (
        <th
          style={{
            padding: '8px 12px',
            textAlign: 'left',
            fontWeight: 'bold',
            fontSize: '0.9em',
            color: '#2d3748',
          }}
          {...props}
        />
      ),
      td: ({ node, ...props }: any) => (
        <td
          style={{
            padding: '8px 12px',
            fontSize: '0.9em',
          }}
          {...props}
        />
      ),
    }),
    [PreComponent]
  );

  return (
    <Box fontSize={fontSize}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
}
